/**
 * Incident Reports — shared contract (API <-> web). Single source of truth for
 * field names, limits, allowed file types and the "required to submit" rule.
 *
 * Rules mirrored from the API (`apps/api/src/modules/incidents/**`), which is
 * authoritative; the web may reuse `findMissingIncidentFields` /
 * `validateIncidentAttachmentLocally` ONLY to give early feedback.
 *
 *  - Actor identity (`createdById`, uploader) is never accepted from a body.
 *  - A report is DRAFT (editable by its author) until submitted; SUBMITTED is
 *    immutable (no field or attachment changes).
 *  - Attachments: at most MAX_INCIDENT_ATTACHMENTS per report, each at most
 *    MAX_INCIDENT_ATTACHMENT_BYTES, PDF/JPEG/PNG/WEBP only. "File Upload"
 *    (PDF) and "Image Upload" (JPEG/PNG/WEBP) share this single limit.
 *  - Free text must never be written to logs (it can contain patient data).
 */
import { z } from 'zod';
import { INCIDENT_CASE_STATUSES, INCIDENT_CLASSIFICATIONS, INCIDENT_CYCLE_KINDS, INCIDENT_SLA_FILTERS, INCIDENT_TYPES, INCIDENT_URGENCIES, } from './incident-workflow.js';
export const MAX_INCIDENT_ATTACHMENTS = 5;
export const MAX_INCIDENT_ATTACHMENT_BYTES = 5 * 1024 * 1024;
/** Server-sniffed MIME types accepted for an incident attachment. */
export const INCIDENT_ALLOWED_MIME_TYPES = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];
/** Image Upload accepts only these; File Upload accepts application/pdf. */
export const INCIDENT_IMAGE_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
export const INCIDENT_STATUSES = ['DRAFT', 'SUBMITTED'];
export const INCIDENT_STAFF_INVOLVED = ['DOCTOR', 'NURSE', 'NON_CLINICAL_STAFF', 'OTHERS'];
/** mine = reports I filed; involved = incidents I am a stakeholder of / my workgroup's; all = every submitted report. */
export const INCIDENT_LIST_SCOPES = ['mine', 'involved', 'all'];
export const INCIDENT_ATTACHMENT_KINDS = ['IMAGE', 'FILE'];
/** Max lengths (also enforced by CHECK constraints in the database). */
export const INCIDENT_LIMITS = {
    /** unitName (snapshot), raisedBy, mpiIdOrPatientName, incidentClassification, category, incidentType */
    shortText: 200,
    /** description, impact, immediateCorrection, correctiveAction, preventiveAction */
    longText: 5000,
    /** why1..why5 */
    why: 2000,
};
/** Exact form labels (user's PDF). */
export const INCIDENT_FORM_LABELS = {
    title: 'Incident Report',
    unitName: 'Unit Name',
    occurredDate: 'Date of Incident Occurrence',
    occurredTime: 'Time of Incident Occurrence',
    reportedDate: 'Date of Incident Reporting',
    reportedTime: 'Time of Incident Reporting',
    raisedBy: 'Incident Raised by',
    mpiIdOrPatientName: 'MPI ID/Patient Name',
    staffInvolved: 'Staff Involved',
    incidentClassification: 'Incident Classification',
    category: 'Category',
    description: 'Describe the Incident',
    impact: 'Impact on Patient, Staff, Infrastructure, Equipment, Process',
    immediateCorrection: 'Immediate Correction Done',
    rcaHeader: 'Root Cause Analysis (5 Why Analysis):',
    rcaHelp: '(Please connect with your Unit Quality In-charge to complete this)',
    why1: 'Why 1',
    why2: 'Why 2',
    why3: 'Why 3',
    why4: 'Why 4',
    why5: 'Why 5',
    correctiveAction: 'Corrective Action',
    preventiveAction: 'Preventive Action',
    incidentType: 'Type of Incident',
    fileUpload: 'File Upload',
    imageUpload: 'Image Upload',
};
export const INCIDENT_STAFF_LABELS = {
    DOCTOR: 'Doctor',
    NURSE: 'Nurse',
    NON_CLINICAL_STAFF: 'Non-clinical Staff',
    OTHERS: 'Others',
};
/**
 * Fields that must be present for DRAFT -> SUBMITTED (server-enforced). The
 * Root Cause Analysis block (why1..why5, corrective/preventive action) is
 * completed with the Unit Quality In-charge and is NOT required at submit;
 * `mpiIdOrPatientName` is optional because not every incident involves a patient.
 */
export const INCIDENT_REQUIRED_FOR_SUBMIT = [
    'unitName',
    'occurredAt',
    'reportedAt',
    'raisedBy',
    'staffInvolved',
    'incidentClassification',
    'category',
    'description',
    'impact',
    'immediateCorrection',
    'incidentType',
];
// ---------------------------------------------------------------------------
// Request DTOs
// ---------------------------------------------------------------------------
/** Trimmed text; an empty string means "no value" (null). */
const text = (max) => z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v.length === 0 ? null : v))
    .nullable();
const instant = () => z.string().datetime({ offset: true }).nullable();
const staff = () => z
    .array(z.enum(INCIDENT_STAFF_INVOLVED))
    .max(INCIDENT_STAFF_INVOLVED.length)
    .transform((a) => [...new Set(a)]);
/**
 * The unit is picked from the Office Locations master list: the client sends
 * only the location id (never free text); the server resolves it, checks it
 * exists in the caller's organisation and is active, and stores the display
 * snapshot in `unitName`. "" / null clears it.
 */
const officeLocationRef = () => z.union([z.string().uuid(), z.literal('').transform(() => null), z.null()]);
const incidentFieldShape = {
    officeLocationId: officeLocationRef(),
    /** Optional floor (Admin → Incident management → Floors). Same rules as the unit: id only, must be active. */
    floorId: officeLocationRef(),
    occurredAt: instant(),
    reportedAt: instant(),
    raisedBy: text(INCIDENT_LIMITS.shortText),
    mpiIdOrPatientName: text(INCIDENT_LIMITS.shortText),
    staffInvolved: staff(),
    incidentClassification: text(INCIDENT_LIMITS.shortText),
    category: text(INCIDENT_LIMITS.shortText),
    description: text(INCIDENT_LIMITS.longText),
    impact: text(INCIDENT_LIMITS.longText),
    immediateCorrection: text(INCIDENT_LIMITS.longText),
    why1: text(INCIDENT_LIMITS.why),
    why2: text(INCIDENT_LIMITS.why),
    why3: text(INCIDENT_LIMITS.why),
    why4: text(INCIDENT_LIMITS.why),
    why5: text(INCIDENT_LIMITS.why),
    correctiveAction: text(INCIDENT_LIMITS.longText),
    preventiveAction: text(INCIDENT_LIMITS.longText),
    incidentType: text(INCIDENT_LIMITS.shortText),
};
/** `POST /incidents` — every field optional; the draft can be saved at any point. Unknown keys are rejected. */
export const createIncidentDtoSchema = z.object(incidentFieldShape).partial().strict();
/**
 * `PATCH /incidents/:id` (DRAFT only) — omitted = unchanged, `null` or "" = clear.
 * At least one field is required.
 */
export const updateIncidentDtoSchema = z
    .object(incidentFieldShape)
    .partial()
    .strict()
    .refine((v) => Object.keys(v).length > 0, { message: 'at least one field is required' });
/**
 * Server-side filters shared by `GET /incidents`, `GET /incidents/summary`
 * and `GET /incidents/export`. Dates are ISO instants (the client turns
 * "day / month / year" into a range in its own time zone) and filter on the
 * incident OCCURRENCE date/time.
 */
export const incidentFilterShape = {
    occurredFrom: z.string().datetime({ offset: true }).optional(),
    occurredTo: z.string().datetime({ offset: true }).optional(),
    officeLocationId: z.string().uuid().optional(),
    floorId: z.string().uuid().optional(),
    workgroupId: z.string().uuid().optional(),
    classification: z.enum(INCIDENT_CLASSIFICATIONS).optional(),
    incidentType: z.enum(INCIDENT_TYPES).optional(),
    urgency: z.enum(INCIDENT_URGENCIES).optional(),
    stakeholderId: z.string().uuid().optional(),
    caseStatus: z.enum(INCIDENT_CASE_STATUSES).optional(),
    cycleKind: z.enum(INCIDENT_CYCLE_KINDS).optional(),
    sla: z.enum(INCIDENT_SLA_FILTERS).optional(),
    /** Incident number (e.g. "INC-000123" or "123"). */
    q: z.string().trim().max(30).optional(),
};
/** `GET /incidents` */
export const incidentListQueryDtoSchema = z.object({
    scope: z.enum(INCIDENT_LIST_SCOPES).default('mine'),
    status: z.enum(INCIDENT_STATUSES).optional(),
    ...incidentFilterShape,
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(25),
});
/** `GET /incidents/summary` — dashboard counts over the same filters (never drafts). */
export const incidentSummaryQueryDtoSchema = z.object({
    scope: z.enum(INCIDENT_LIST_SCOPES).default('all'),
    ...incidentFilterShape,
});
/** `GET /incidents/export` */
export const INCIDENT_EXPORT_FORMATS = ['xlsx', 'pdf'];
export const incidentExportQueryDtoSchema = z.object({
    format: z.enum(INCIDENT_EXPORT_FORMATS),
    scope: z.enum(INCIDENT_LIST_SCOPES).default('all'),
    /** IANA zone used to print dates in the file. */
    tz: z.string().max(64).default('Asia/Kolkata'),
    ...incidentFilterShape,
});
// ---------------------------------------------------------------------------
// Pure helpers shared by API and web
// ---------------------------------------------------------------------------
export function incidentAttachmentKind(mime) {
    return INCIDENT_IMAGE_MIME_TYPES.includes(mime) ? 'IMAGE' : 'FILE';
}
/** Which required-to-submit fields are still empty. Empty array = ready to submit. */
export function findMissingIncidentFields(incident) {
    return INCIDENT_REQUIRED_FOR_SUBMIT.filter((field) => {
        const v = incident[field];
        if (v === null || v === undefined)
            return true;
        if (typeof v === 'string')
            return v.trim().length === 0;
        if (v instanceof Date)
            return Number.isNaN(v.getTime());
        return v.length === 0;
    });
}
/** Early client-side check only; the server re-validates (and sniffs the real content type). */
export function validateIncidentAttachmentLocally(file, existingCount) {
    if (existingCount >= MAX_INCIDENT_ATTACHMENTS)
        return 'too_many';
    if (file.size === 0)
        return 'empty';
    if (file.size > MAX_INCIDENT_ATTACHMENT_BYTES)
        return 'too_large';
    if (!INCIDENT_ALLOWED_MIME_TYPES.includes(file.type))
        return 'type_not_allowed';
    return null;
}
// ---------------------------------------------------------------------------
// Incident submission email (Admin -> Settings -> Incident report notifications)
// ---------------------------------------------------------------------------
import { z as zIncidentNotify } from 'zod';
/** GET/PUT /admin/incident-notifications. Recipients receive the FULL report by email on submit. */
export const incidentNotificationSettingsSchema = zIncidentNotify.object({
    recipients: zIncidentNotify.array(zIncidentNotify.string().trim().toLowerCase().email().max(254)).max(20),
});
/** "CODE" or "CODE — Name": how an office location is shown (dropdown option and unit snapshot). */
export function officeLocationLabel(l) {
    const name = l.name?.trim();
    return name ? `${l.code} — ${name}` : l.code;
}
//# sourceMappingURL=incident-dto.js.map